// CAL-08: three unambiguous views. Legacy URL value "execution" is parsed as
// "laboral" in calendarUtils.parseCalendarViewParam; navigation semantics are unchanged.
export type CalendarLayout = "month" | "week" | "day" | "agenda";

export function parseCalendarLayout(value: string | null | undefined): CalendarLayout {
  return value === "week" || value === "day" || value === "agenda" ? value : "month";
}

export type CalendarMode = "planning" | "real" | "laboral";

type CalendarNavigationEvent = {
  id?: unknown;
  resource?: {
    installationId?: unknown;
  };
};

type CalendarNavigationLogger = (
  message: "CALENDAR_EVENT_WITHOUT_VALID_INSTALLATION_ID",
  details: { eventId: string | number | null; calendarMode: CalendarMode },
) => void;

// CAL-07: recoverable calendar context carried to installation detail and back.
export type CalendarContextParams = {
  view: CalendarMode;
  date: string | null;
  technician: string | null;
  layout?: CalendarLayout;
  workId?: number | null;
  workQuery?: string;
};

export type CalendarNavigationContext = {
  currentDate?: Date | null;
  technicianFilter?: string | null;
  layout?: CalendarLayout;
  workId?: number | null;
  workQuery?: string;
};

function sanitizeEventId(eventId: unknown): string | number | null {
  if (typeof eventId === "string" || typeof eventId === "number") {
    return eventId;
  }

  return null;
}

function sanitizeCalendarMode(mode: string | null): CalendarMode | null {
  if (mode === "planning" || mode === "real" || mode === "laboral" || mode === "execution") {
    return mode === "execution" ? "laboral" : mode;
  }
  return null;
}

export function formatCalendarDateParam(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function parseCalendarDateParam(value: string | null | undefined): Date | null {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const parts = value.split("-").map(Number);
  if (parts.length !== 3 || parts.some((part) => !Number.isInteger(part))) return null;
  const date = new Date(parts[0], parts[1] - 1, parts[2]);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function buildCalendarContextQuery(
  calendarMode: CalendarMode,
  currentDate?: Date | null,
  technicianFilter?: string | null,
  layout?: CalendarLayout,
  work?: { workId?: number | null; workQuery?: string },
): string {
  const params = new URLSearchParams();
  params.set("view", calendarMode);
  if (currentDate instanceof Date && !Number.isNaN(currentDate.getTime())) {
    params.set("date", formatCalendarDateParam(currentDate));
  }
  const technician = typeof technicianFilter === "string" && technicianFilter.trim() !== ""
    ? technicianFilter.trim()
    : "all";
  params.set("tech", technician);
  if (layout) params.set("layout", parseCalendarLayout(layout));
  if (work?.workId && Number.isSafeInteger(work.workId) && work.workId > 0) params.set("work", String(work.workId));
  if (work?.workQuery) params.set("q", work.workQuery.slice(0, 200));
  return params.toString();
}

export function parseCalendarContextParams(search: string): CalendarContextParams | null {
  const params = new URLSearchParams(search);
  const view = sanitizeCalendarMode(params.get("view"));
  if (!view) return null;
  const dateParam = params.get("date");
  const technicianParam = params.get("tech");
  const workParam = params.get("work");
  const workId = workParam && /^\d+$/.test(workParam) ? Number(workParam) : null;
  return {
    view,
    ...(params.has("layout") ? { layout: parseCalendarLayout(params.get("layout")) } : {}),
    ...(Number.isSafeInteger(workId) && Number(workId) > 0 ? { workId: Number(workId) } : {}),
    ...(params.has("q") ? { workQuery: (params.get("q") ?? "").slice(0, 200) } : {}),
    date: parseCalendarDateParam(dateParam) ? dateParam : null,
    technician: technicianParam && (/^\d+$/.test(technicianParam) || technicianParam === "all")
      ? technicianParam
      : null,
  };
}

export function buildCalendarReturnPath(context: CalendarContextParams): string {
  const params = new URLSearchParams();
  params.set("view", context.view);
  if (context.date) params.set("date", context.date);
  if (context.technician) params.set("tech", context.technician);
  if (context.layout) params.set("layout", parseCalendarLayout(context.layout));
  if (context.workId) params.set("work", String(context.workId));
  if (context.workQuery) params.set("q", context.workQuery.slice(0, 200));
  return `/calendar?${params.toString()}`;
}

export function getCalendarEventInstallationPath(
  event: CalendarNavigationEvent,
  calendarMode: CalendarMode,
  logError: CalendarNavigationLogger = console.error,
) {
  const resourceInstallationId = Number(event.resource?.installationId);

  if (Number.isInteger(resourceInstallationId) && resourceInstallationId > 0) {
    return `/installations/${resourceInstallationId}`;
  }

  const legacyInstallationId = typeof event.id === "number" ? event.id : Number.NaN;

  if (Number.isInteger(legacyInstallationId) && legacyInstallationId > 0) {
    return `/installations/${legacyInstallationId}`;
  }

  logError("CALENDAR_EVENT_WITHOUT_VALID_INSTALLATION_ID", {
    eventId: sanitizeEventId(event.id),
    calendarMode,
  });

  return null;
}

// CAL-07: same navigation contract as getCalendarEventInstallationPath, plus a
// recoverable calendar context in the query string so detail can link back.
export function getCalendarEventInstallationPathFromCalendar(
  event: CalendarNavigationEvent,
  calendarMode: CalendarMode,
  context: CalendarNavigationContext = {},
  logError: CalendarNavigationLogger = console.error,
): string | null {
  const basePath = getCalendarEventInstallationPath(event, calendarMode, logError);
  if (!basePath) return null;
  const contextQuery = buildCalendarContextQuery(calendarMode, context.currentDate, context.technicianFilter, context.layout, context);
  return `${basePath}?${contextQuery}`;
}
